import React, { useState, useEffect } from "react";

const GSTCalculator = () => {
  const [originalAmount, setOriginalAmount] = useState(10000);
  const [gstRate, setGstRate] = useState(18);
  const [calculationType, setCalculationType] = useState("exclusive");
  const [includecess, setIncludeCess] = useState(false);
  const [cessRate, setCessRate] = useState(0);
  const [result, setResult] = useState({
    originalAmount: "0.00",
    gstRate: "0.00",
    cgst: "0.00",
    sgst: "0.00",
    igst: "0.00",
    cess: "0.00",
    totalGST: "0.00",
    finalAmount: "0.00",
    netAmount: "0.00",
    savingTip: "",
  });

  // GST Calculation Function
  const calculateGST = () => {
    const amount = parseFloat(originalAmount);
    const rate = parseFloat(gstRate);
    const cessPercent = parseFloat(cessRate);

    if (isNaN(amount) || amount < 0 || isNaN(rate) || rate < 0) return;

    let baseAmount = amount;
    let gstAmount = 0;
    let cessAmount = 0;
    let finalAmount = 0;
    let netAmount = 0;

    if (calculationType === "exclusive") {
      // GST Exclusive - Add GST to the amount
      baseAmount = amount;
      gstAmount = (amount * rate) / 100;
      cessAmount = includecess ? (amount * cessPercent) / 100 : 0;
      finalAmount = amount + gstAmount + cessAmount;
      netAmount = amount;
    } else {
      // GST Inclusive - Extract GST from the amount
      finalAmount = amount;
      baseAmount = (amount * 100) / (100 + rate + (includecess ? cessPercent : 0));
      gstAmount = (baseAmount * rate) / 100;
      cessAmount = includecess ? (baseAmount * cessPercent) / 100 : 0;
      netAmount = baseAmount;
    }

    // Split GST into CGST and SGST (each is half of total GST)
    const cgst = gstAmount / 2;
    const sgst = gstAmount / 2;
    const igst = gstAmount; // For interstate transactions

    let savingTip = "";
    if (rate <= 5) {
      savingTip = "Low GST rate - Essential goods/services category";
    } else if (rate <= 12) {
      savingTip = "Standard GST rate - Common goods/services";
    } else if (rate <= 18) {
      savingTip = "Standard GST rate - Most goods and services";
    } else if (rate <= 28) {
      savingTip = "Highest GST slab - Luxury and sin goods";
    }

    setResult({
      originalAmount: baseAmount.toFixed(2),
      gstRate: rate.toFixed(2),
      cgst: cgst.toFixed(2),
      sgst: sgst.toFixed(2),
      igst: igst.toFixed(2),
      cess: cessAmount.toFixed(2),
      totalGST: gstAmount.toFixed(2),
      finalAmount: finalAmount.toFixed(2),
      netAmount: netAmount.toFixed(2),
      savingTip,
    });
  };

  useEffect(() => {
    calculateGST();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <h1>GST Calculator (India)</h1>
          <p>
            Calculate GST (Goods and Services Tax) with CGST, SGST, IGST breakdown for your transactions
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Transaction Details
              </h2>

              {/* Original Amount */}
              <div className="input-group">
                <label>Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={originalAmount}
                    onChange={(e) => setOriginalAmount(e.target.value)}
                    className="calc-input"
                    placeholder="10000"
                  />
                </div>
                <div className="quick-select-group">
                  {[1000, 5000, 10000, 50000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOriginalAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calculation Type */}
              <div className="input-group">
                <label>Calculation Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "exclusive", label: "GST Exclusive (Add GST)" },
                    { value: "inclusive", label: "GST Inclusive (Remove GST)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setCalculationType(type.value)}
                      className={`quick-select-btn ${calculationType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* GST Rate */}
              <div className="input-group">
                <label>GST Rate (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={gstRate}
                    onChange={(e) => setGstRate(e.target.value)}
                    className="calc-input"
                    placeholder="18"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 5, 12, 18, 28].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setGstRate(rate)}
                      className={`quick-select-btn ${gstRate === rate ? 'success' : 'secondary'}`}
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Include Cess */}
              <div className="input-group">
                <label>Include Cess?</label>
                <div className="quick-select-group">
                  {[
                    { value: false, label: "No Cess" },
                    { value: true, label: "Add Cess" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setIncludeCess(type.value)}
                      className={`quick-select-btn ${includecess === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cess Rate */}
              {includecess && (
                <div className="input-group">
                  <label>Cess Rate (%)</label>
                  <div className="input-wrapper">
                    <span className="input-prefix">%</span>
                    <input
                      type="number"
                      value={cessRate}
                      onChange={(e) => setCessRate(e.target.value)}
                      className="calc-input"
                      placeholder="1"
                    />
                  </div>
                  <div className="quick-select-group">
                    {[1, 3, 5, 12].map((rate) => (
                      <button
                        key={rate}
                        onClick={() => setCessRate(rate)}
                        className="quick-select-btn success"
                      >
                        {rate}%
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={calculateGST} className="calculate-btn">
                Calculate GST
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                GST Calculation
              </h2>

              <div className="result-card">
                {/* Final Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Final Amount {calculationType === "exclusive" ? "(Inc. GST)" : "(Total)"}</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.finalAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.savingTip}
                  </div>
                </div>

                {/* Net Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">{calculationType === "exclusive" ? "Original Amount" : "Net Amount (Exc. GST)"}</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.netAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Base amount
                  </div>
                </div>

                {/* Total GST */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total GST ({result.gstRate}%)</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalGST).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    GST amount
                  </div>
                </div>

                {/* CGST & SGST */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">CGST + SGST (Intrastate)</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.cgst).toLocaleString("en-IN")} + ₹{Number(result.sgst).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Central + State GST
                  </div>
                </div>

                {/* IGST */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">IGST (Interstate)</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.igst).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Integrated GST
                  </div>
                </div>

                {/* Cess */}
                {includecess && parseFloat(result.cess) > 0 && (
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Cess ({cessRate}%)</span>
                      <div className="result-icon-wrapper purple">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                          <line x1="12" y1="1" x2="12" y2="23"></line>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value">
                      ₹{Number(result.cess).toLocaleString("en-IN")}
                    </div>
                    <div className="result-subtext">
                      Additional cess
                    </div>
                  </div>
                )}

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Amount Distribution</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Base Amount</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.netAmount) / parseFloat(result.finalAmount)) * 100).toFixed(2)}% (₹{Number(result.netAmount).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.netAmount) / parseFloat(result.finalAmount)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Total GST</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.totalGST) / parseFloat(result.finalAmount)) * 100).toFixed(2)}% (₹{Number(result.totalGST).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.totalGST) / parseFloat(result.finalAmount)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  {includecess && parseFloat(result.cess) > 0 && (
                    <div className="breakdown-item">
                      <div className="breakdown-header">
                        <span className="breakdown-label">Cess</span>
                        <span className="breakdown-percentage">
                          {((parseFloat(result.cess) / parseFloat(result.finalAmount)) * 100).toFixed(2)}% (₹{Number(result.cess).toLocaleString("en-IN")})
                        </span>
                      </div>
                      <div className="breakdown-bar">
                        <div
                          className="breakdown-bar-fill purple"
                          style={{
                            width: `${(parseFloat(result.cess) / parseFloat(result.finalAmount)) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* GST Info Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>GST Breakdown Info</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    For intrastate supply, GST is split equally into CGST (Central GST) and SGST (State GST). For interstate supply, IGST (Integrated GST) is charged. {result.savingTip}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding GST in India</h3>
              <p>
                GST (Goods and Services Tax) is an indirect tax levied on the supply of goods and services in India. 
                Implemented on July 1, 2017, GST replaced multiple indirect taxes. It has four main tax slabs: 5%, 12%, 
                18%, and 28%, with essential items at 0% and luxury/sin goods at 28% plus cess. For intrastate 
                transactions (within the same state), GST is split equally into CGST (Central GST) collected by the 
                central government and SGST (State GST) collected by the state government. For interstate transactions 
                (between different states), IGST (Integrated GST) is charged, which is collected by the central 
                government and later distributed between states. Some goods like petrol, alcohol, and electricity are 
                currently outside GST purview.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>GST Calculation Formula:</h4>
            <div className="formula-display">
              GST Exclusive: Final Amount = Base Amount + GST + Cess<br/>
              GST Inclusive: Base Amount = Total Amount / (1 + GST Rate + Cess Rate)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">GST Amount</div>
                <div className="formula-param-value">Base Amount × GST Rate / 100</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">CGST & SGST</div>
                <div className="formula-param-value">Each is 50% of total GST (for intrastate)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">IGST</div>
                <div className="formula-param-value">Equal to total GST (for interstate)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Cess</div>
                <div className="formula-param-value">Base Amount × Cess Rate / 100</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is the difference between CGST, SGST, and IGST?</h5>
              <p>
                CGST and SGST apply to intrastate transactions (same state), split equally. IGST applies to interstate transactions (different states), collected by central government.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the different GST tax slabs?</h5>
              <p>
                GST has 4 main slabs: 5% (essentials), 12% (processed foods), 18% (standard goods/services), and 28% (luxury items). Some items are exempt (0%).
              </p>
            </div>

            <div className="faq-item">
              <h5>What is GST Cess and when is it applicable?</h5>
              <p>
                Cess is an additional tax over GST on luxury, sin, and demerit goods like tobacco, aerated drinks, and luxury cars. Rates vary by product category.
              </p>
            </div>

            <div className="faq-item">
              <h5>When should I use GST Exclusive vs Inclusive calculation?</h5>
              <p>
                Use Exclusive when you know the base price and want to add GST. Use Inclusive when the total amount includes GST and you want to extract the base amount.
              </p>
            </div>

            <div className="faq-item">
              <h5>Who needs to register for GST?</h5>
              <p>
                Businesses with turnover above ₹40 lakh (₹20 lakh for special category states) must register. E-commerce sellers and interstate suppliers need GST registration regardless of turnover.
              </p>
            </div>

            <div className="faq-item">
              <h5>How often should GST returns be filed?</h5>
              <p>
                Regular taxpayers file GSTR-1 (outward supplies) and GSTR-3B (summary return) monthly. Composition scheme taxpayers file quarterly. Annual return GSTR-9 is due by December 31.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GSTCalculator;